// The Wi-Fi networks in range — the list you pick from instead of retyping an
// SSID from memory.
//
// Backed by the app-local Android plugin in
// android/app/src/main/java/com/smartify/smartlifeindia/WifiScanPlugin.java.
// A browser cannot enumerate Wi-Fi networks at all (no web API exists, by
// design), so on desktop this reports unsupported and every caller must have a
// typed-SSID path anyway.

import { Capacitor, registerPlugin } from '@capacitor/core';

const WifiScan = registerPlugin('WifiScan');

export function wifiScanSupported() {
  return Capacitor.isNativePlatform();
}

// Reject codes worth telling apart in the UI. Everything else is a plain
// failure the user can only respond to by retrying.
export const WIFI_OFF = 'WIFI_OFF';
export const PERMISSION_DENIED = 'PERMISSION_DENIED';

// The installed APK predates the scan plugin.
//
// ⚠️ THIS IS A REAL AND EXPECTED STATE, NOT A DEFENSIVE FLOURISH. OTA ships web
// assets only — it cannot add a Capacitor plugin or a permission (see
// docs/09-release-and-ota.md). So a phone can perfectly well be running this
// JavaScript on an APK that has no WifiScan in it, and it must say "update the
// app", not "scan failed": one is fixable by the user, the other sends them
// looking at their router.
export const PLUGIN_MISSING = 'PLUGIN_MISSING';

// Capacitor words this differently per platform and version, so match on all of
// the shapes rather than one exact string.
function isMissingPlugin(err) {
  if (err?.code === 'UNIMPLEMENTED') return true;
  const m = String(err?.message || '').toLowerCase();
  return m.includes('not implemented')
    || m.includes('does not have')
    || m.includes('not found')
    || m.includes('unimplemented');
}

// Returns { networks: [{ ssid, level, frequency, band, secured }] }, strongest
// first. Throws with `.code` set to one of the constants above.
//
// The sort is here rather than in Java because "strongest first" is a
// presentation choice — the plugin's job is to report what the radio saw.
export async function scanNetworks() {
  if (!wifiScanSupported()) {
    const e = new Error('Wi-Fi scanning needs the installed app.');
    e.code = 'UNSUPPORTED';
    throw e;
  }
  try {
    const res = await WifiScan.scan();
    const list = Array.isArray(res?.networks) ? res.networks : [];
    return [...list].sort((a, b) => (b.level ?? -999) - (a.level ?? -999));
  } catch (err) {
    if (isMissingPlugin(err)) {
      const e = new Error('This version of the app cannot scan for Wi-Fi networks.');
      e.code = PLUGIN_MISSING;
      throw e;
    }
    const e = new Error(err?.message || 'Wi-Fi scan failed.');
    e.code = err?.code || 'SCAN_FAILED';
    throw e;
  }
}

// A device's own setup hotspot is named "smartify<uniqueNumber>" — that number
// IS its unique number, which is how the Flutter add-device radar identifies a
// brand-new device without anyone typing anything.
const SETUP_AP = /^smartify(\d+)$/i;

export function isSetupHotspot(ssid) {
  return SETUP_AP.test(String(ssid || '').trim());
}

export function unoFromHotspot(ssid) {
  const m = SETUP_AP.exec(String(ssid || '').trim());
  return m ? m[1] : null;
}

// Split a scan into "devices waiting to be set up" and "networks a device could
// be told to join". A setup hotspot is never a candidate to join: it is the
// device itself, and handing a device its own AP would strand it.
export function partitionNetworks(networks) {
  const setup = [];
  const usable = [];
  (networks || []).forEach((n) => (isSetupHotspot(n.ssid) ? setup : usable).push(n));
  return { setup, usable };
}
